// Friendly text for error codes we can classify with confidence from HTTP semantics alone,
// independent of whether the throwing bpm-lambdas handler has adopted BaseAppError (DEV-24577)
// yet. The typed 4xx classes from bpm-lambdas lib/errors.ts (ValidationError, NotFoundError,
// ForbiddenError, ConflictError) are deliberately NOT mapped: their messages are written for the
// user ("cid is required", "Order 12 not found"), so a generic replacement would hide the useful
// part (DEV-26221). A 4xx with an unrecognized code falls through to the caller's own message
// unchanged -- those are overwhelmingly hand-authored, caller-facing text (e.g. "cid is required"),
// not a leak risk. An unrecognized 5xx falls back to GENERIC_SERVER_ERROR_MESSAGE below instead (DEV-24578):
// a server-side failure is never safe to show verbatim as-is (it may be a raw pg/SQL error or an
// internal config name), but the original text is never discarded -- `setError` (redux/modals.ts)
// always keeps it as rawMessage, recoverable via the dialog's "More info" disclosure.
const ERROR_CODE_MESSAGES: Record<string, string> = {
  TooManyRequestsError: 'Too many requests. Please wait a moment and try again.',
  UpstreamServiceError:
    'A service we depend on is not responding right now. Please try again in a moment.'
};

// BaseAppError is what wrapCaughtError returns for a failure it cannot classify, often a transient
// 503. It is also thrown directly with a user-facing 4xx message, so it maps only when status is
// 5xx or unknown.
const SERVER_ERROR_CODE_MESSAGES: Record<string, string> = {
  BaseAppError: 'This request could not be completed right now. Please try again in a moment.'
};

const GENERIC_SERVER_ERROR_MESSAGE =
  'Something went wrong on our end. Please try again or contact support if the issue persists.';

export const getCodeFriendlyMessage = (code?: string, status?: number): string | undefined => {
  if (!code) return undefined;
  if (ERROR_CODE_MESSAGES[code]) return ERROR_CODE_MESSAGES[code];
  return status === undefined || status >= 500 ? SERVER_ERROR_CODE_MESSAGES[code] : undefined;
};

export const getFriendlyErrorMessage = (code?: string, status?: number): string | undefined =>
  getCodeFriendlyMessage(code, status) ??
  (status !== undefined && status >= 500 ? GENERIC_SERVER_ERROR_MESSAGE : undefined);
